import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmberOutlined";
import {
  MONO_FONT,
  PAGE_FONT,
  SectionHead,
  headCellSx,
  numCellSx,
  textCellSx,
} from "../../components/metrics/metricsUi";
import { formatCount } from "../../components/metrics/metricsFormat";
import { formatDate } from "../../utils/formatDate";
import { isMobile } from "../../globalStyles/mobile";
import { barWidthPercent } from "./returnsAnalysisUi";

const formatRatePct = (rate) =>
  rate == null || Number.isNaN(Number(rate)) ? "—" : `${(Number(rate) * 100).toFixed(1)}%`;

const formatSharePct = (share) =>
  share == null || Number.isNaN(Number(share)) ? "—" : `${Number(share).toFixed(1)}%`;

const unitsLabel = (n) => `${formatCount(n)} ${Number(n) === 1 ? "unit" : "units"}`;

const returnsLabel = (n) => `${formatCount(n)} ${Number(n) === 1 ? "return" : "returns"}`;

const Stat = ({ label, value, ui }) => (
  <Box>
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 600,
        color: ui.colors.textTertiary,
        letterSpacing: "0.03em",
        textTransform: "uppercase",
        mb: 0.4,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: 16,
        fontWeight: 650,
        fontVariantNumeric: "tabular-nums",
        color: ui.colors.textPrimary,
        fontFamily: MONO_FONT,
        wordBreak: "break-word",
      }}
    >
      {value}
    </Typography>
  </Box>
);

const commonReasons = (reasons = []) => {
  const totals = new Map();
  reasons.forEach((row) => {
    (row.raw_reasons || []).forEach((raw) => {
      if (!raw?.reason) return;
      totals.set(raw.reason, (totals.get(raw.reason) || 0) + (Number(raw.returned_units) || 0));
    });
  });
  return [...totals.entries()]
    .map(([reason, returned_units]) => ({ reason, returned_units }))
    .sort(
      (a, b) =>
        b.returned_units - a.returned_units || a.reason.localeCompare(b.reason),
    );
};

const SkuReturnInvestigationDrawer = ({
  open,
  onClose,
  ui,
  sku,
  payload,
  loading,
  error,
  selectedReason,
  onSelectReason = () => {},
  onBackToReasons = () => {},
  evidence,
  evidenceLoading,
  evidenceError,
  onRetryEvidence,
  evidenceSort,
  onEvidenceSort,
  evidencePage,
  evidenceRowsPerPage,
  onEvidencePageChange,
  onEvidenceRowsPerPageChange,
  onReturnClick,
}) => {
  const { colors } = ui;
  const summary = payload?.summary;
  const reasons = payload?.reasons || [];
  const examples = commonReasons(reasons);
  const returnedUnits = summary?.returned_units || 0;
  const evidenceMode = Boolean(selectedReason);
  const evidenceRows = evidence?.data || [];
  const evidenceSummary = evidence?.summary;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile() ? "100%" : evidenceMode ? 880 : 520,
          maxWidth: "100%",
          backgroundColor: ui.darkMode ? colors.bgPrimary : "#F5F6F8",
          fontFamily: PAGE_FONT,
        },
      }}
    >
      <Box sx={{ px: 2.5, pt: 2, pb: 1.25, display: "flex", alignItems: "flex-start" }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <SectionHead
            title="SKU Return Investigation"
            subtitle={
              evidenceMode
                ? "Evidence behind the selected customer reason."
                : "Why customers return this SKU in the selected period."
            }
          />
        </Box>
        <IconButton onClick={onClose} aria-label="Close SKU investigation" size="small" sx={{ mt: 0.25 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ px: 2.5, pb: 3, overflowY: "auto" }}>
        {error && !evidenceMode && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>
            {error}
          </Alert>
        )}

        <Box sx={{ ...ui.card, px: 2, py: 1.75, mb: 2 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: isMobile() ? "1fr 1fr" : "1.4fr 1fr 1fr 1fr",
              gap: 1.5,
            }}
          >
            <Stat ui={ui} label="SKU" value={sku || payload?.sku || "—"} />
            <Stat ui={ui} label="Returned Units" value={formatCount(summary?.returned_units)} />
            <Stat ui={ui} label="Units Sold" value={formatCount(summary?.units_sold)} />
            <Stat ui={ui} label="Return Rate" value={formatRatePct(summary?.return_rate)} />
          </Box>
        </Box>

        {evidenceMode ? (
          <>
            <Button
              onClick={onBackToReasons}
              startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
              sx={{
                mb: 1.5,
                px: 0,
                minWidth: 0,
                fontSize: 12.5,
                fontWeight: 600,
                textTransform: "none",
                color: colors.textSecondary,
              }}
            >
              Back to reasons
            </Button>

            {evidenceError && (
              <Alert
                severity="error"
                sx={{ mb: 2, borderRadius: "10px" }}
                action={
                  onRetryEvidence ? (
                    <Button color="inherit" size="small" onClick={onRetryEvidence}>
                      Retry
                    </Button>
                  ) : null
                }
              >
                {evidenceError}
              </Alert>
            )}

            <Box sx={{ ...ui.card, px: 2, pt: 1.5, pb: 1.25, mb: 2 }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: colors.textTertiary,
                  letterSpacing: "0.03em",
                  textTransform: "uppercase",
                  mb: 0.75,
                }}
              >
                Selected reason
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 650, color: colors.textPrimary, mb: 0.75 }}>
                {selectedReason}
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  fontVariantNumeric: "tabular-nums",
                  fontFamily: MONO_FONT,
                  color: colors.textSecondary,
                }}
              >
                {evidence
                  ? `${unitsLabel(evidenceSummary?.returned_units)} · ${returnsLabel(
                      evidenceSummary?.return_count,
                    )}`
                  : evidenceLoading
                    ? "Loading evidence…"
                    : ""}
              </Typography>
            </Box>

            <Box sx={{ ...ui.card, px: 1.25, pt: 1, pb: 0.5 }}>
              {evidenceLoading && !evidence ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : evidenceRows.length === 0 && !evidenceLoading ? (
                <Typography sx={{ fontSize: 13, color: colors.textTertiary, px: 1, py: 3 }}>
                  No matching returns were found for this reason.
                </Typography>
              ) : (
                <>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headCellSx(ui)}>Return</TableCell>
                        <TableCell sx={headCellSx(ui)}>Order</TableCell>
                        <TableCell
                          sx={headCellSx(ui, { cursor: "pointer" })}
                          onClick={() => onEvidenceSort("requested_at")}
                        >
                          <TableSortLabel
                            active={evidenceSort?.field === "requested_at"}
                            direction={evidenceSort?.field === "requested_at" ? evidenceSort.dir : "desc"}
                          >
                            Requested
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={headCellSx(ui)}>Stage</TableCell>
                        <TableCell
                          sx={headCellSx(ui, { textAlign: "right", cursor: "pointer" })}
                          onClick={() => onEvidenceSort("returned_units")}
                        >
                          <TableSortLabel
                            active={evidenceSort?.field === "returned_units"}
                            direction={
                              evidenceSort?.field === "returned_units" ? evidenceSort.dir : "desc"
                            }
                          >
                            Units
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={headCellSx(ui)}>Channel</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {evidenceRows.map((row) => (
                        <TableRow
                          key={row._id}
                          hover
                          sx={{ cursor: "pointer" }}
                          onClick={() => onReturnClick(row)}
                        >
                          <TableCell sx={textCellSx(ui)}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <Typography sx={{ fontSize: 13 }}>
                                {row.return_number || "—"}
                              </Typography>
                              {row.has_exceptions ? (
                                <Tooltip title="Has exceptions">
                                  <WarningAmberIcon sx={{ fontSize: 14, color: ui.warning }} />
                                </Tooltip>
                              ) : null}
                            </Box>
                          </TableCell>
                          <TableCell sx={textCellSx(ui)}>{row.order_number || "—"}</TableCell>
                          <TableCell sx={textCellSx(ui)}>
                            {row.requested_at ? formatDate(row.requested_at, "—") : "—"}
                          </TableCell>
                          <TableCell sx={textCellSx(ui)}>
                            {row.stage_label || row.stage || "—"}
                          </TableCell>
                          <TableCell sx={numCellSx(ui)}>{formatCount(row.returned_units)}</TableCell>
                          <TableCell sx={textCellSx(ui)}>{row.channel || "Unknown"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <TablePagination
                    component="div"
                    count={evidence?.total || 0}
                    page={evidencePage}
                    onPageChange={(_, next) => onEvidencePageChange(next)}
                    rowsPerPage={evidenceRowsPerPage}
                    onRowsPerPageChange={(e) =>
                      onEvidenceRowsPerPageChange(parseInt(e.target.value, 10))
                    }
                    rowsPerPageOptions={[10, 25, 50]}
                    sx={{ fontSize: 12.5 }}
                  />
                </>
              )}
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ ...ui.card, px: 2, pt: 1.5, pb: 1.75, mb: 2 }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: colors.textTertiary,
                  letterSpacing: "0.03em",
                  textTransform: "uppercase",
                  mb: 1.25,
                }}
              >
                Why customers return this SKU
              </Typography>

              {loading && !payload ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : reasons.length === 0 && !loading ? (
                <Typography sx={{ fontSize: 13, color: colors.textTertiary, py: 2 }}>
                  No returned units for this SKU in this period.
                </Typography>
              ) : (
                reasons.map((row) => {
                  const width = barWidthPercent(row.returned_units, returnedUnits);
                  return (
                    <Box key={row.category} sx={{ mb: 1.35 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 0.45 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary }}>
                          {row.category}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 12.5,
                            fontWeight: 600,
                            fontVariantNumeric: "tabular-nums",
                            color: colors.textSecondary,
                            fontFamily: MONO_FONT,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {unitsLabel(row.returned_units)}
                          <Box component="span" sx={{ color: colors.textTertiary, fontWeight: 500, ml: 1 }}>
                            {formatSharePct(row.share_pct)}
                          </Box>
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          height: 8,
                          borderRadius: "4px",
                          backgroundColor: ui.surfaceAlt,
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          data-testid={`reason-bar-${row.category}`}
                          sx={{
                            width: `${width}%`,
                            height: "100%",
                            backgroundColor: ui.accent,
                          }}
                        />
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>

            {examples.length > 0 && (
              <Box sx={{ ...ui.card, px: 2, pt: 1.5, pb: 1.25 }}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: colors.textTertiary,
                    letterSpacing: "0.03em",
                    textTransform: "uppercase",
                    mb: 1,
                  }}
                >
                  Common reasons
                </Typography>
                {examples.map((row) => (
                  <Box
                    key={row.reason}
                    role="button"
                    tabIndex={0}
                    onClick={() => onSelectReason(row.reason)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onSelectReason(row.reason);
                      }
                    }}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 1.5,
                      py: 0.7,
                      borderBottom: `1px solid ${colors.border}`,
                      cursor: "pointer",
                      "&:last-of-type": { borderBottom: "none" },
                      "&:hover": { color: colors.textPrimary },
                    }}
                  >
                    <Typography sx={{ fontSize: 13, color: colors.textPrimary, lineHeight: 1.4 }}>
                      {row.reason}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 12.5,
                        fontWeight: 650,
                        fontVariantNumeric: "tabular-nums",
                        fontFamily: MONO_FONT,
                        color: colors.textSecondary,
                      }}
                    >
                      {formatCount(row.returned_units)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default SkuReturnInvestigationDrawer;
export { commonReasons, formatRatePct, formatSharePct };
